/**
 * Refund credits from credit_history, keyed by market ticker.
 */
import { useQuery } from '@tanstack/react-query';

import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { Refund } from 'models/credit';
import type { GetCreditHistoryResponse } from 'models/reqres';
import config from 'utils/config';

import { removeEmpty } from './utils';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

const CREDIT_HISTORY_PAGE_LIMIT = 1000;

export const refundKeys = {
    all: ['refunds'] as const,
    user: (userId: string) => [...refundKeys.all, userId] as const,
};

async function fetchRefundsByTicker(userId: string, signal?: AbortSignal): Promise<Record<string, Refund>> {
    const refundsByTicker: Record<string, Refund> = {};
    let cursor: string | undefined;
    do {
        const response = await api.get<GetCreditHistoryResponse>(`/users/${userId}/credit_history`, {
            params: removeEmpty({ limit: CREDIT_HISTORY_PAGE_LIMIT, cursor }),
            signal,
        });
        for (const credit of response.data.credits ?? []) {
            if (credit.type !== 'refund') continue;
            refundsByTicker[credit.reason.toUpperCase()] ??= {
                amountCents: credit.amount_cents,
                createdAt: credit.created_at,
                reason: credit.reason,
                status: credit.status,
                type: 'refund',
            };
        }
        cursor = response.data.cursor || undefined;
    } while (cursor);
    return refundsByTicker;
}

export function useRefundsQuery(enabled = true) {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);
    return useQuery({
        queryKey: refundKeys.user(userId ?? ''),
        queryFn: ({ signal }) => fetchRefundsByTicker(userId!, signal),
        enabled: !!userId && isLoggedInMember && enabled,
        staleTime: Infinity,
    });
}

export function useRefundByMarketTicker(marketTicker: string | null | undefined): Refund | undefined {
    return useRefundsQuery(!!marketTicker).data?.[marketTicker?.toUpperCase() ?? ''];
}
